import type { CSSProperties, ReactNode } from 'react';
import { VisuallyHidden } from '@mantine/core';
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from '@tabler/icons-react';
import { TOKENS, TYPE } from '../theme';
import { MonoLabel } from './MonoLabel';
import { Sparkline } from './Sparkline';
import { staggerStyle, useCountUp } from './motion';

/**
 * A percentage change as a chip: green and up, red and down, grey when flat.
 * Signed, one decimal, with the arrow as a second cue for anyone who cannot
 * tell the two colours apart.
 */
export function DeltaChip({ value }: { value: number }) {
  const flat = !Number.isFinite(value) || Math.abs(value) < 0.05;
  const up = value > 0;
  const color = flat ? TOKENS.textSecondary : up ? TOKENS.greenText : TOKENS.redText;
  const background = flat ? TOKENS.surface2 : up ? TOKENS.greenSoft : TOKENS.redSoft;
  const Icon = flat ? IconMinus : up ? IconArrowUpRight : IconArrowDownRight;
  const text = flat ? '0.0%' : `${up ? '+' : '−'}${Math.abs(value).toFixed(1)}%`;
  return (
    <span
      style={{
        ...TYPE.monoFigure,
        fontSize: 11,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '2px 6px 2px 4px',
        borderRadius: 6,
        color,
        background,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={12} stroke={2.25} aria-hidden />
      {text}
    </span>
  );
}

export interface StatTileProps {
  label: string;
  value: number;
  /** Formats the (possibly fractional, mid-animation) value for display. */
  format: (value: number) => string;
  /** Percentage change against the previous period. Omit when there is none. */
  delta?: number;
  /** What the delta compares against: "vs previous 7 days". */
  deltaLabel?: string;
  /** A line under the figure when there is no delta to show. */
  caption?: ReactNode;
  /** A trend to draw under the figure. Two points or more. */
  spark?: number[];
  sparkTone?: 'ink' | 'positive';
  /** A 0 to 1 meter in place of a sparkline, for a figure that is a rate. */
  meter?: number;
  /** Position in an entrance stagger. */
  index?: number;
  style?: CSSProperties;
}

/**
 * One headline figure: a mono label, the number in Funnel Display counting up
 * to its value, then a delta or caption, then an optional sparkline.
 *
 * Designed to sit in a StatGrid, which draws the rules between tiles; on its
 * own it is unframed.
 */
export function StatTile({
  label, value, format, delta, deltaLabel, caption, spark, sparkTone = 'ink', meter, index = 0, style,
}: StatTileProps) {
  const shown = useCountUp(value);
  return (
    <div
      className="hf-rise"
      style={staggerStyle(index, {
        padding: '18px 20px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        ...style,
      })}
    >
      <MonoLabel component="div">{label}</MonoLabel>
      <div style={{ ...TYPE.display, fontSize: 30, color: TOKENS.text, whiteSpace: 'nowrap' }}>
        {/* The counting figure is decoration; a screen reader gets the
            settled value once, not thirty intermediate ones. */}
        <span aria-hidden>{format(shown)}</span>
        <VisuallyHidden>{format(value)}</VisuallyHidden>
      </div>
      <div
        style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 8, rowGap: 4,
          minHeight: 20, fontSize: 12, lineHeight: 1.4, color: TOKENS.textMuted, minWidth: 0,
        }}
      >
        {delta !== undefined ? (
          <>
            <DeltaChip value={delta} />
            {deltaLabel && (
              <span style={{ whiteSpace: 'nowrap' }}>{deltaLabel}</span>
            )}
          </>
        ) : (
          caption && (
            <span>{caption}</span>
          )
        )}
      </div>
      {/* Reserve the sparkline's height in every tile, so the row's figures
          and captions line up whether or not a tile has a trend to draw. */}
      <div className="hf-fade hf-spark-slot" style={staggerStyle(index, { height: 32, marginTop: 'auto' })}>
        {spark ? (
          <Sparkline values={spark} tone={sparkTone} />
        ) : meter !== undefined ? (
          <Meter fraction={meter} index={index} />
        ) : null}
      </div>
    </div>
  );
}

/** A thin green rate bar sitting on the tile's baseline. */
function Meter({ fraction, index }: { fraction: number; index: number }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ width: '100%', height: 4, borderRadius: 4, background: TOKENS.surface2, overflow: 'hidden' }}>
        <div
          className="hf-bar"
          style={staggerStyle(index, {
            height: '100%',
            borderRadius: 4,
            background: TOKENS.green,
            transform: `scaleX(${Math.max(0, Math.min(1, fraction))})`,
          })}
        />
      </div>
    </div>
  );
}

/**
 * Tiles separated by hairlines inside one rounded frame, like the site's
 * speed-stats. `columns` applies from 1024px; below that it is two up, then
 * one.
 */
export function StatGrid({ children, columns = 5 }: { children: ReactNode; columns?: number }) {
  return (
    <div className="hf-stat-grid" style={{ '--hf-cols': columns } as CSSProperties}>
      {children}
    </div>
  );
}
